// Predefined color palette for todo lists (5-4-3 row layout)
export const LIST_COLORS = [
  // First row (5 colors) - Primary vibrant colors
  { name: 'Blue', value: '#2563EB', light: '#EFF6FF' },
  { name: 'Red', value: '#DC2626', light: '#FEF2F2' },
  { name: 'Green', value: '#16A34A', light: '#F0FDF4' },
  { name: 'Purple', value: '#9333EA', light: '#FAF5FF' },
  { name: 'Orange', value: '#EA580C', light: '#FFF7ED' },
  
  // Second row (4 colors) - Complementary vibrant colors
  { name: 'Pink', value: '#EC4899', light: '#FDF2F8' },
  { name: 'Teal', value: '#0891B2', light: '#F0FDFA' },
  { name: 'Indigo', value: '#4F46E5', light: '#EEF2FF' },
  { name: 'Emerald', value: '#059669', light: '#ECFDF5' },
  
  // Third row (3 colors) - Warm accent colors
  { name: 'Rose', value: '#F43F5E', light: '#FFF1F2' },
  { name: 'Amber', value: '#F59E0B', light: '#FFFBEB' },
  { name: 'Yellow', value: '#CA8A04', light: '#FEFCE8' },
];

export const getDefaultListColor = () => LIST_COLORS[0]; // Blue as default

// Helper function to convert hex to RGB
const hexToRgb = (hex: string) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
};

// Helper function to generate a light version of any color
const generateLightColor = (colorValue: string) => {
  const rgb = hexToRgb(colorValue);
  if (rgb) {
    // Create a very light version by mixing with white
    const lightR = Math.round(rgb.r + (255 - rgb.r) * 0.9);
    const lightG = Math.round(rgb.g + (255 - rgb.g) * 0.9);
    const lightB = Math.round(rgb.b + (255 - rgb.b) * 0.9);
    return `rgb(${lightR}, ${lightG}, ${lightB})`;
  }
  return '#F9FAFB'; // Default light gray
};

export const getListColorByValue = (colorValue: string) => {
  // First try exact match
  const exactMatch = LIST_COLORS.find(color => color.value === colorValue);
  if (exactMatch) {
    return exactMatch;
  }
  
  // If no exact match, return a dynamic color object using the provided value
  // This ensures we always use the actual color from the backend
  return {
    name: 'Custom',
    value: colorValue,
    light: generateLightColor(colorValue)
  };
};

export const getRandomListColor = () => {
  return LIST_COLORS[Math.floor(Math.random() * LIST_COLORS.length)];
};

// Helper function to get a funny comment about color choice
export const getColorComment = (colorValue: string): string => {
  const color = getListColorByValue(colorValue);
  
  const comments: { [key: string]: string } = {
    'Blue': "Cool choice! 🧊 Blue like the ocean depths where your productivity will dive!",
    'Red': "Fiery! 🔥 Red hot tasks coming through - handle with care!",
    'Green': "Fresh! 🌱 Green means go - your todo list is about to grow!",
    'Purple': "Royal! 👑 Purple for the productivity royalty you are!",
    'Orange': "Zesty! 🍊 Orange you glad you picked such a vibrant color?",
    'Pink': "Pretty in pink! 🌸 Your tasks will bloom beautifully!",
    'Teal': "Teal-icious! 🦚 A sophisticated choice for a sophisticated mind!",
    'Indigo': "Deep thoughts! 🌌 Indigo like the midnight sky of endless possibilities!",
    'Emerald': "Precious! 💎 Your tasks are as valuable as emeralds now!",
    'Rose': "Rosy outlook! 🌹 Everything's coming up roses with this choice!",
    'Amber': "Golden hour! ⚡ Amber alert: productivity levels rising!",
    'Yellow': "Sunny side up! ☀️ Yellow brings the sunshine to your workflow!"
  };
  
  return comments[color.name] || `Nice pick! 🎨 ${color.name} is looking fantastic!`;
};
